import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PRICING_LISTS, pricingListPath } from '../pricingRoutes';
import { refusalOf, type Refusal } from '../lib/refusals';
import type { PricingListRow } from '../pricingListsApi';
import { RefusalNotice } from './RefusalNotice';

/**
 * Naming a rate card — the whole of creating one.
 *
 * Two fields, and that is deliberate: the point of a card is that it is a thing
 * a venue can name and then pick by name from a resource. Asking for rates here
 * as well would put the ten-minute decision ("what does an hour of PS5 cost, on
 * which days, at which priority") in front of somebody whose actual question is
 * "I am adding my first PlayStation and the select is empty". The rates go on
 * afterwards, in the card's own editor, which is also where they go the second
 * time — one place, not two.
 *
 * `MaxLength(120)` on the name and `MaxLength(500)` on the description are the
 * DTO's, restated on the inputs so the browser refuses the 121st character
 * rather than the API refusing the save.
 */
const NAME_LIMIT = 120;
const DESCRIPTION_LIMIT = 500;

export function PricingListFormDialog({
  open,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** The card being renamed, or null to create one. */
  record: PricingListRow | null;
  onClose: () => void;
  /**
   * The saved card.
   *
   * Handed back rather than merely announced, because the caller that most needs
   * this is the attach control on a resource form: someone with no cards at all
   * creates one here and must come back with it already chosen. Dropping them
   * onto an empty select again is the dead end this whole change is about.
   */
  onSaved: (list: PricingListRow) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(record?.name ?? '');
    setDescription(record?.description ?? '');
    setRefusal(null);
    setNameError(null);
  }, [open, record]);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t('spaces.pricingRules.lists.nameRequired'));
      return;
    }
    setNameError(null);
    setSaving(true);
    setRefusal(null);
    try {
      // `description` travels as `null` rather than being omitted when it is
      // cleared: a PATCH that leaves the key out keeps whatever was stored, so
      // deleting the text would look like it saved and change nothing.
      const body = { name: trimmed, description: description.trim() || null };
      const response = record
        ? await api.patch(pricingListPath(record.id), body)
        : await api.post(PRICING_LISTS, body);
      toast.success(
        t(record ? 'spaces.pricingRules.lists.renamed' : 'spaces.pricingRules.lists.created', {
          name: trimmed,
        }),
      );
      onSaved(response.data as PricingListRow);
      onClose();
    } catch (error) {
      const failure = refusalOf(error);
      // A 409 with no `code` on these two routes is the duplicate-name refusal
      // and can be nothing else — the other two conflicts here (`…LIST_IN_USE`,
      // `…LIST_DEACTIVATED`) both carry one. Naming it beside the field is worth
      // the inference: "A price list called Standard already exists" is the one
      // thing the manager can act on, and the generic sentence would hide it.
      if (failure.status === 409 && !failure.code) {
        setNameError(t('spaces.pricingRules.lists.nameTaken', { name: trimmed }));
      } else {
        setRefusal(failure);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(
        record ? 'spaces.pricingRules.lists.editTitle' : 'spaces.pricingRules.lists.newTitle',
        { name: record?.name ?? '' },
      )}
      description={t('spaces.pricingRules.lists.formHint')}
      className="max-w-md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? t('settings.common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('spaces.pricingRules.lists.name')}
          hint={t('spaces.pricingRules.lists.nameHint')}
          error={nameError ?? undefined}
          required
        >
          <Input
            value={name}
            dir="auto"
            maxLength={NAME_LIMIT}
            placeholder={t('spaces.pricingRules.lists.namePlaceholder')}
            onChange={(event) => setName(event.currentTarget.value)}
          />
        </Field>

        <Field
          label={t('spaces.pricingRules.lists.description')}
          hint={t('spaces.pricingRules.lists.descriptionHint')}
        >
          <Input
            value={description}
            dir="auto"
            maxLength={DESCRIPTION_LIMIT}
            onChange={(event) => setDescription(event.currentTarget.value)}
          />
        </Field>

        {refusal && <RefusalNotice refusal={refusal} tone="danger" />}
      </div>
    </Dialog>
  );
}
